import { useState } from "react";
import { ROADMAP } from "@/constants/testIds/roadmap";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CTA() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrMsg("");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="cta"
      data-testid={ROADMAP.ctaSection}
      className="py-20 lg:py-32 bg-[#0A0A0A] text-white border-b border-[#0A0A0A]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/60">
            <span className="font-mono text-[#FF4F00]">/ 10</span>
            <span className="font-semibold">Get started</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold mt-3 leading-[1.05]">
            Ready to run your operations
            <br />
            on <span className="text-[#FF4F00]">Gr8Boost ERP</span>?
          </h2>
          <p className="mt-6 max-w-xl text-white/70 text-lg leading-relaxed">
            Book a tailored walkthrough with our implementation team. We&apos;ll map your workflows to
            Gr8Boost ERP modules and show you Gr8Agentic AI in action — using your own data.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-0 border border-white/15">
            <div className="p-5 border-r border-white/15">
              <div className="text-xs uppercase tracking-[0.2em] text-[#FF4F00] font-mono">
                Time-to-value
              </div>
              <div className="font-heading text-3xl font-bold mt-2 tracking-tighter">
                12 weeks
              </div>
            </div>
            <div className="p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[#FF4F00] font-mono">
                Hypercare
              </div>
              <div className="font-heading text-3xl font-bold mt-2 tracking-tighter">
                2 weeks
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6">
          <form
            onSubmit={submit}
            data-testid={ROADMAP.demoForm}
            className="border border-white/15 bg-white/[0.03] p-6 lg:p-8"
          >
            <h3 className="font-heading text-xl font-semibold tracking-tight mb-6">
              Request a demo
            </h3>

            {status === "success" ? (
              <div
                data-testid="demo-success"
                className="flex items-start gap-3 border border-[#0B7A75] bg-[#0B7A75]/10 p-5"
              >
                <CheckCircle2 size={20} className="text-[#0B7A75] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Thank you — request received.</div>
                  <div className="text-sm text-white/70 mt-1">
                    Our team will reach out within one business day.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Full name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    testid={ROADMAP.demoFormName}
                  />
                  <Field
                    label="Work email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    testid={ROADMAP.demoFormEmail}
                  />
                </div>
                <div className="mt-4">
                  <Field
                    label="Company"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    required
                    testid={ROADMAP.demoFormCompany}
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-xs uppercase tracking-[0.15em] text-white/60 mb-2">
                    What would you like to see?
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    data-testid={ROADMAP.demoFormMessage}
                    placeholder="Modules of interest, team size, current pain points..."
                    className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF4F00] focus:outline-none transition-colors"
                  />
                </div>

                {status === "error" && (
                  <div
                    data-testid="demo-error"
                    className="mt-4 text-sm text-[#FF4F00] border border-[#FF4F00] bg-[#FF4F00]/10 px-4 py-2"
                  >
                    {errMsg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid={ROADMAP.demoFormSubmit}
                  className="mt-6 inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#E64600] disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium px-6 py-3.5 rounded-sm transition-colors duration-200"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>Book a walkthrough →</>
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-20 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-[0.2em] text-white/40">
        <div>Gr8Boost ERP with Gr8Agentic AI · v1.0 Roadmap</div>
        <div>© Gigmann Medicals × AHQ Solutions blueprint</div>
      </div>
    </section>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-white/60 mb-2">{label}</span>
      <input
        {...rest}
        data-testid={testid}
        className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF4F00] focus:outline-none transition-colors"
      />
    </label>
  );
}
